import { EGamePeriod, EStatusMatches } from '@/app/pages/sports/football/enums';
import { MatchApi } from '@/app/pages/sports/football/home/models';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  signal,
} from '@angular/core';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { differenceInMinutes, format } from 'date-fns';

@Component({
  selector: 'app-status-match',
  standalone: true,
  imports: [],
  templateUrl: './status-match.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatusMatchComponent implements OnDestroy {
  match = input.required<MatchApi>();
  urlIcon = environment.urlIcon;

  private translate = inject(TranslateService);

  private now = signal(new Date());
  private intervalId: any;

  private internalMatchStatus = signal<EStatusMatches | null>(null);

  readonly autoEndMatch = computed(() => {
    const currentDate = this.now();
    const match = this.match();
    const matchStart = match.PeriodStartTime
      ? new Date(match.PeriodStartTime)
      : new Date(match.Date);

    const minutesElapsed = differenceInMinutes(currentDate, matchStart);
    const totalMinutes = minutesElapsed + (match.GameTimeAdd || 0);

    if (
      match.Status === EStatusMatches.NORMAL &&
      match.GamePeriod === EGamePeriod.SECOND_HALF &&
      totalMinutes >= 90
    ) {
      this.internalMatchStatus.set(EStatusMatches.ENDED);
    }

    return null;
  });

  ngOnInit() {
    // actualiza el tiempo actual cada 60 segundos
    this.intervalId = setInterval(() => {
      this.now.set(new Date());
    }, 60000);
  }

  ngOnDestroy() {
    clearInterval(this.intervalId);
  }

  readonly viewClockIcon = computed(() => {
    return this.match().Status === EStatusMatches.UPCOMING;
  });

  readonly isLiveTimerVisible = computed(() => {
    const currentDate = this.now();
    const matchDate = new Date(this.match().Date);
    const status = this.match().Status;

    if (status !== EStatusMatches.NORMAL) return false;
    if (matchDate > currentDate) return false;

    if (this.match().GamePeriod) {
      return (
        this.match().GamePeriod === EGamePeriod.FIRST_HALF ||
        this.match().GamePeriod === EGamePeriod.SECOND_HALF
      );
    }

    return false;
  });
  readonly isLiveStatusVisible = computed(() => {
    const currentDate = this.now();
    const matchDate = new Date(this.match().Date);
    const status = this.match().Status;

    if (status !== EStatusMatches.NORMAL) return false;
    if (matchDate > currentDate) return false;

    if (this.match().GamePeriod) {
      return (
        this.match().GamePeriod === EGamePeriod.FIRST_HALF ||
        this.match().GamePeriod === EGamePeriod.SECOND_HALF ||
        this.match().GamePeriod === EGamePeriod.HALF_TIME ||
        this.match().GamePeriod === EGamePeriod.OVERTIME ||
        this.match().GamePeriod === EGamePeriod.PENALTY_SHOOT_OUT
      );
    }

    return false;
  });

  readonly viewStatusMatch = computed(() => {
    const currentDate = this.now();

    const matchDate =
      this.match().PeriodStartTime !== null
        ? new Date(this.match().PeriodStartTime!)
        : new Date(this.match().Date);

    const status = this.internalMatchStatus() || this.match().Status;

    const minutesElapsed = differenceInMinutes(currentDate, matchDate);

    switch (status) {
      case EStatusMatches.CANCELED:
      case EStatusMatches.DELAYED:
      case EStatusMatches.ENDED:
      case EStatusMatches.INTERRUPTED:
      case EStatusMatches.TO_BE_DEFINED:
        return this.translate.instant(`STATUS_MATCH_${status}`);
      case EStatusMatches.UPCOMING:
        return format(matchDate, 'HH:mm');
      case EStatusMatches.NORMAL:
        if (this.match().GamePeriod && this.match().GamePeriod !== null) {
          return this.handleStatusLiveMatch(
            this.match().GamePeriod!,
            minutesElapsed,
            this.match().GameTimeAdd !== null ? this.match().GameTimeAdd! : 0
          );
        }

        if (matchDate > currentDate) {
          return format(matchDate, 'HH:mm');
        }

        return format(matchDate, 'HH:mm');
      default:
        return '';
    }
  });

  handleStatusLiveMatch(
    statusLive: EGamePeriod,
    minutesElapsed: number,
    additionalTime: number
  ): string {
    switch (statusLive) {
      case EGamePeriod.FIRST_HALF:
        return `${minutesElapsed}'`;

      case EGamePeriod.HALF_TIME:
        return this.translate.instant('halftime');

      case EGamePeriod.SECOND_HALF:
        return `${minutesElapsed + additionalTime}'`;

      case EGamePeriod.OVERTIME:
        return this.translate.instant('Overtime');

      case EGamePeriod.PENALTY_SHOOT_OUT:
        return this.translate.instant('Penalty');

      default:
        return `${minutesElapsed}'`;
    }
  }
}
